"use client";
import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// ─── Stats Data ───────────────────────────────────────────────────────────────
// value: target number to count up to
// suffix: text appended after the number (%, +, etc.)
// translationKey: label under the number
const STATS = [
  { value: 83, suffix: "%", translationKey: "leapStatsSection.stats.fasterHiring" },
  { value: 91, suffix: "%", translationKey: "leapStatsSection.stats.costSavings" },
  { value: 95, suffix: "%", translationKey: "leapStatsSection.stats.qualityScore" },
  { value: 50, suffix: "+", translationKey: "leapStatsSection.stats.languages" },
];

// ─── Styled Components ──────────────────────────────────────────────────────
const StatsSection = styled(Box)(() => ({
  backgroundColor: "#15101F",
  padding: "32px 0",
  borderTop: "1px solid rgba(255,255,255,0.06)",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
}));

const StatsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-around",
  alignItems: "center",
  gap: "24px",
  [theme.breakpoints.down("sm")]: {
    justifyContent: "center",
  },
}));

const StatItem = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minWidth: "140px",
}));

const StatNumber = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "30px",
  color: "#9048FB",
  marginBottom: "4px",
}));

const StatLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "14px",
  fontWeight: 500,
  color: "#A6A2B3",
  textAlign: "center",
}));

// ─── Count-up hook: animates 0 → target once the element is in view ────────
const useCountUp = (target, isVisible, duration = 1500) => {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isVisible || startedRef.current) return;
    startedRef.current = true;

    let startTime = null;
    let frameId;

    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // ease-out cubic for a smooth finish
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));

      if (progress < 1) {
        frameId = requestAnimationFrame(step);
      }
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [isVisible, target, duration]);

  return value;
};

// ─── Single stat with its own count-up value ────────────────────────────────
const StatCounter = ({ stat, isVisible, isRTL, t }) => {
  const count = useCountUp(stat.value, isVisible);

  return (
    <StatItem>
      <StatNumber isRTL={isRTL}>
        {count}
        {stat.suffix}
      </StatNumber>
      <StatLabel isRTL={isRTL}>{t(stat.translationKey)}</StatLabel>
    </StatItem>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
const LeapStatsSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <StatsSection ref={sectionRef}>
      <Container maxWidth="xl">
        <StatsRow>
          {STATS.map((stat, idx) => (
            <StatCounter
              key={idx}
              stat={stat}
              isVisible={isVisible}
              isRTL={isRTL}
              t={t}
            />
          ))}
        </StatsRow>
      </Container>
    </StatsSection>
  );
};

export default LeapStatsSection;